import axios from "axios";
import {
  mockDoctors,
  mockPatients,
  mockAppointments,
  mockMedicines,
  mockOrders,
  mockPayments,
  mockPrescriptions,
  mockMedicalReports,
  mockSpecializations,
  mockTimeSlots,
  mockLabTestCategories,
  mockLabTests,
  mockDeliveryBoys,
  dashboardStats,
  mockDepositRequests,
  mockLabBookings,
  LAB_BOOKING_STATUSES,
  mockSupportTickets,
} from "../data/mockData";

const delay = (ms = 400) => new Promise((res) => setTimeout(res, ms));

const api = axios.create({ baseURL: "https://root.medihub1.co.in/api" });
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const msg = err?.response?.data?.message || "";
    if (err?.response?.status === 401 || msg === "Invalid or expired token") {
      localStorage.removeItem("token");
      localStorage.removeItem("medihub_admin_auth");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  },
);

const mockService = (data) => ({
  getAll: async (params = {}) => {
    await delay();
    let result = [...data];
    if (params.search)
      result = result.filter((i) =>
        Object.values(i)
          .join(" ")
          .toLowerCase()
          .includes(params.search.toLowerCase()),
      );
    return result;
  },
  getById: async (id) => {
    await delay();
    return data.find((i) => i.id === id || i.id === parseInt(id));
  },
  create: async (payload) => {
    await delay();
    const item = { ...payload, id: Date.now() };
    data.push(item);
    return item;
  },
  update: async (id, payload) => {
    await delay();
    const idx = data.findIndex((i) => i.id === id || i.id === parseInt(id));
    if (idx > -1) {
      data[idx] = { ...data[idx], ...payload };
      return data[idx];
    }
  },
  delete: async (id) => {
    await delay();
    const idx = data.findIndex((i) => i.id === id || i.id === parseInt(id));
    if (idx > -1) data.splice(idx, 1);
    return true;
  },
});

export const doctorService = {
  ...mockService(mockDoctors),
  getAll: async (params = {}) => {
    const { data } = await api.get("admin/doctors", { params });
    console.log("doctor ka data:", data);
    return data?.data?.doctors || [];
  },
  getById: async (id) => {
    const { data } = await api.get(`admin/doctors/${id}`);
    return data?.data || null;
  },
  create: async (payload) => {
    const fd = new FormData();
    fd.append("name", payload.name);
    fd.append("email", payload.email);
    fd.append("mobile", payload.mobile);
    fd.append("password", "1111111111");
    fd.append("specialization", payload.specialization);
    fd.append("qualifications", payload.qualifications || "");
    fd.append("experience_years", payload.experience_years ?? "");
    fd.append("consultation_fee", payload.consultation_fee ?? "");
    fd.append("consultancy_type", payload.consultancy_type || "online");
    fd.append("address", payload.address || "");
    fd.append("city", payload.city || "");
    fd.append("commission_percent", payload.commission_percent ?? "");
    fd.append("status", payload.status || "active");
    fd.append("registration_number", payload.registration_number || "");
    fd.append("medical_council_name", payload.medical_council_name || "");
    fd.append("clinic_name", payload.clinic_name || "");
    fd.append("clinic_phone", payload.clinic_phone || "");
    fd.append("bio", payload.bio || "");
    if (payload.profile_img instanceof File)
      fd.append("profile_img", payload.profile_img);
    if (payload.registration_certificate_url instanceof File)
      fd.append(
        "registration_certificate_url",
        payload.registration_certificate_url,
      );
    if (payload.degree_certificate_url instanceof File)
      fd.append("degree_certificate_url", payload.degree_certificate_url);
    if (payload.id_proof_url instanceof File)
      fd.append("id_proof_url", payload.id_proof_url);
    const { data } = await api.post("doctors", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  update: async (id, payload) => {
    const fd = new FormData();
    const fields = [
      "name",
      "email",
      "mobile",
      "specialization",
      "qualifications",
      "experience_years",
      "consultation_fee",
      "consultancy_type",
      "address",
      "city",
      "commission_percent",
      "status",
      "registration_number",
      "medical_council_name",
      "clinic_name",
      "clinic_phone",
      "bio",
    ];
    fields.forEach((f) => {
      if (payload[f] != null && payload[f] !== "") fd.append(f, payload[f]);
    });
    if (payload.profile_img instanceof File)
      fd.append("profile_img", payload.profile_img);
    if (payload.registration_certificate_url instanceof File)
      fd.append(
        "registration_certificate_url",
        payload.registration_certificate_url,
      );
    if (payload.degree_certificate_url instanceof File)
      fd.append("degree_certificate_url", payload.degree_certificate_url);
    if (payload.id_proof_url instanceof File)
      fd.append("id_proof_url", payload.id_proof_url);
    const { data } = await api.put(`doctors/${id}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  delete: async (id) => {
    const token = localStorage.getItem("token");
    const { data } = await api.delete(`doctors/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  },
  verify: async (id, status) => {
    await delay();
    const doc = mockDoctors.find((d) => d.id === id);
    if (doc) {
      doc.verified = status === "approved";
      doc.status = status === "approved" ? "active" : "inactive";
    }
    return doc;
  },
  toggleStatus: async (id, status) => {
    const { data } = await api.patch(`admin/doctors/${id}/status`, { status });
    return data;
  },
  toggleVerified: async (id, is_verified) => {
    const { data } = await api.patch(`admin/doctors/${id}/status`, {
      is_verified,
    });
    return data;
  },
  getAvailability: async (id) => {
    const { data } = await api.get(`doctors/${id}/availability`);
    return data?.data?.availability || [];
  },
  saveAvailability: async (id, availability) => {
    const { data } = await api.post(`doctors/${id}/admin/availability`, {
      availability,
    });
    return data;
  },
  getAppointments: async (doctorId) => {
    await delay();
    return mockAppointments.filter((a) => a.doctorId === parseInt(doctorId));
  },
  getPrescriptions: async (doctorId) => {
    await delay();
    return mockPrescriptions.filter((p) => p.doctorId === parseInt(doctorId));
  },
  requestWithdraw: async (doctorId, amount) => {
    await delay();
    const doc = mockDoctors.find((d) => d.id === parseInt(doctorId));
    if (!doc) throw new Error("Doctor not found");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) throw new Error("Invalid amount");
    if (amt > doc.walletBalance)
      throw new Error("Amount exceeds wallet balance");
    const req = {
      id: `WR-${String(Date.now()).slice(-4)}`,
      amount: amt,
      status: "pending",
      date: new Date().toISOString().split("T")[0],
      processedDate: null,
    };
    if (!doc.withdrawRequests) doc.withdrawRequests = [];
    doc.withdrawRequests.unshift(req);
    return req;
  },
  approveWithdraw: async (doctorId, requestId) => {
    await delay();
    const doc = mockDoctors.find((d) => d.id === parseInt(doctorId));
    if (!doc) throw new Error("Doctor not found");
    const req = doc.withdrawRequests?.find((r) => r.id === requestId);
    if (!req) throw new Error("Request not found");
    if (req.status !== "pending") throw new Error("Request is not pending");
    if (req.amount > doc.walletBalance) throw new Error("Insufficient balance");
    doc.walletBalance = +(doc.walletBalance - req.amount).toFixed(2);
    doc.totalWithdrawn = +((doc.totalWithdrawn || 0) + req.amount).toFixed(2);
    req.status = "approved";
    req.processedDate = new Date().toISOString().split("T")[0];
    return req;
  },
  rejectWithdraw: async (doctorId, requestId) => {
    await delay();
    const doc = mockDoctors.find((d) => d.id === parseInt(doctorId));
    if (!doc) throw new Error("Doctor not found");
    const req = doc.withdrawRequests?.find((r) => r.id === requestId);
    if (!req) throw new Error("Request not found");
    if (req.status !== "pending") throw new Error("Request is not pending");
    req.status = "rejected";
    req.processedDate = new Date().toISOString().split("T")[0];
    return req;
  },
};

export const patientService = {
  ...mockService(mockPatients),
  getAll: async (params = {}) => {
    const { data } = await api.get("users/admin", { params });
    return data?.data?.users || [];
  },
  getById: async (id) => {
    const { data } = await api.get(`users/admin/${id}/full`);
    return data?.data || null;
  },
  create: async (payload) => {
    const fd = new FormData();
    fd.append("name", payload.name);
    fd.append("email", payload.email);
    fd.append("mobile", payload.mobile);
    if (payload.gender) fd.append("gender", payload.gender);
    if (payload.dob) fd.append("dob", payload.dob);
    if (payload.blood_group) fd.append("blood_group", payload.blood_group);
    const { data } = await api.post("users/admin", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  update: async (id, payload) => {
    const { data } = await api.put(`users/admin/${id}`, payload);
    return data;
  },
  toggleStatus: async (id, status) => {
    const { data } = await api.patch(`users/admin/${id}/status`, { status });
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`users/admin/${id}`);
    return data;
  },
  getAppointments: async (patientId) => {
    await delay();
    return mockAppointments.filter((a) => a.patientId === parseInt(patientId));
  },
  getOrders: async (patientId) => {
    await delay();
    return mockOrders.filter((o) => o.patientId === parseInt(patientId));
  },
  getReports: async (patientId) => {
    await delay();
    return mockMedicalReports.filter(
      (r) => r.patientId === parseInt(patientId),
    );
  },
  rechargeWallet: async (patientId, amount, note) => {
    await delay();
    const p = mockPatients.find((p) => p.id === parseInt(patientId));
    if (!p) throw new Error("Patient not found");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) throw new Error("Invalid amount");
    p.walletBalance = +((p.walletBalance || 0) + amt).toFixed(2);
    p.totalDeposited = +((p.totalDeposited || 0) + amt).toFixed(2);
    const txn = {
      id: `TXN-P${String(Date.now()).slice(-4)}`,
      type: "Wallet Recharge",
      amount: amt,
      method: "Admin Credit",
      status: "completed",
      date: new Date().toISOString().split("T")[0],
      reference: `REF-WR${String(Date.now()).slice(-6)}`,
      note: note || "Wallet recharged by admin",
    };
    if (!p.transactions) p.transactions = [];
    p.transactions.unshift(txn);
    return {
      walletBalance: p.walletBalance,
      totalDeposited: p.totalDeposited,
      txn,
    };
  },
  getTransactions: async (patientId) => {
    await delay();
    const p = mockPatients.find((p) => p.id === parseInt(patientId));
    return p?.transactions || [];
  },
  updateTransactionStatus: async (patientId, txnId, newStatus) => {
    await delay();
    const p = mockPatients.find((p) => p.id === parseInt(patientId));
    if (!p) throw new Error("Patient not found");
    const txn = p.transactions?.find((t) => t.id === txnId);
    if (!txn) throw new Error("Transaction not found");
    const prev = txn.status;
    txn.status = newStatus;
    // adjust totalSpent: credit when newly completed, debit when moving away from completed
    if (newStatus === "completed" && prev !== "completed") {
      p.totalSpent = +((p.totalSpent || 0) + txn.amount).toFixed(2);
    } else if (prev === "completed" && newStatus !== "completed") {
      p.totalSpent = +Math.max(0, (p.totalSpent || 0) - txn.amount).toFixed(2);
    }
    return txn;
  },
  refundTransaction: async (patientId, txnId) => {
    await delay();
    const p = mockPatients.find((p) => p.id === parseInt(patientId));
    if (!p) throw new Error("Patient not found");
    const txn = p.transactions?.find((t) => t.id === txnId);
    if (!txn) throw new Error("Transaction not found");
    if (txn.status !== "completed")
      throw new Error("Only completed transactions can be refunded");
    txn.status = "refunded";
    p.totalSpent = +Math.max(0, (p.totalSpent || 0) - txn.amount).toFixed(2);
    return txn;
  },
  createTransaction: async (patientId, txnData) => {
    await delay();
    const p = mockPatients.find((p) => p.id === parseInt(patientId));
    if (!p) throw new Error("Patient not found");
    const txn = {
      id: `TXN-P${String(Date.now()).slice(-4)}`,
      date: new Date().toISOString().split("T")[0],
      reference: `REF-${String(Date.now()).slice(-6)}`,
      ...txnData,
    };
    if (!p.transactions) p.transactions = [];
    p.transactions.unshift(txn);
    if (txn.status === "completed") {
      p.totalSpent = +((p.totalSpent || 0) + txn.amount).toFixed(2);
    }
    return txn;
  },
};

/* ── Commission helper ──────────────────────────────────────────────────────── */
const calcCommission = (fee, type, value) => {
  if (!fee || !type || value == null)
    return { commission: 0, doctorEarning: fee || 0, platformEarning: 0 };
  const commission =
    type === "percentage"
      ? +((fee * value) / 100).toFixed(2)
      : +Math.min(value, fee).toFixed(2);
  return {
    commission,
    doctorEarning: +(fee - commission).toFixed(2),
    platformEarning: commission,
  };
};

export const appointmentService = {
  ...mockService(mockAppointments),
  getAll: async (params = {}) => {
    const { data } = await api.get("appointments/admin", { params });
    return data?.appointments || data?.data?.appointments || [];
  },
  book: async (payload) => {
    const fd = new FormData();
    fd.append("user_id", payload.user_id);
    fd.append("doctor_id", payload.doctor_id);
    fd.append("slot_id", payload.slot_id);
    fd.append("appointment_date", payload.appointment_date);
    fd.append("consultancy_type", payload.consultancy_type);
    if (payload.reason) fd.append("reason", payload.reason);
    if (payload.clinic_note) fd.append("clinic_note", payload.clinic_note);
    if (payload.reports?.length) {
      payload.reports.forEach((f) => fd.append("reports[]", f));
    }
    const { data } = await api.post("appointments/admin/book", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  updateStatus: async (id, payload) => {
    const { data } = await api.post(`appointments/admin/${id}/status`, payload);
    return data;
  },
  reassign: async (id, doctorId, doctorName, fee) => {
    await delay();
    const a = mockAppointments.find(
      (a) => a.id === id || a.id === parseInt(id),
    );
    if (a) {
      a.doctor = doctorName;
      a.doctorId = doctorId;
      if (fee) a.fee = fee;
    }
    return a;
  },
};

export const medicineService = {
  ...mockService(mockMedicines),
  getAll: async (params = {}) => {
    const { data } = await api.get("medicines/admin", { params });
    return data?.data || [];
  },
  create: async (payload) => {
    const fd = new FormData();
    fd.append("name", payload.name);
    fd.append("price", payload.price);
    fd.append("discounted_price", payload.discounted_price ?? "");
    fd.append("category_id", payload.category_id ?? "");
    fd.append("brand", payload.brand || "");
    fd.append("description", payload.description || "");
    fd.append("discount_percent", payload.discount_percent ?? "");
    fd.append("stock", payload.stock);
    fd.append("requires_prescription", payload.requires_prescription ?? 0);
    fd.append("status", payload.status || "active");
    fd.append("generic_name", payload.generic_name || "");
    fd.append("composition", payload.composition || "");
    fd.append("dosage", payload.dosage || "");
    fd.append("batch_number", payload.batch_number || "");
    fd.append("expiry_date", payload.expiry_date || "");
    if (payload.image_url instanceof File)
      fd.append("image_url", payload.image_url);
    if (payload.medicine_images?.length) {
      payload.medicine_images.forEach((f) => fd.append("medicine_images[]", f));
    }
    const { data } = await api.post("medicines", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  updateStock: async (id, qty) => {
    await delay();
    const m = mockMedicines.find((m) => m.id === id);
    if (m) {
      m.stock = Math.max(0, m.stock + qty);
      m.status =
        m.stock === 0
          ? "out-of-stock"
          : m.stock < 20
            ? "low-stock"
            : "in-stock";
    }
    return m;
  },
  update: async (id, payload) => {
    const fd = new FormData();
    const fields = [
      "name",
      "price",
      "discounted_price",
      "category_id",
      "brand",
      "description",
      "discount_percent",
      "stock",
      "requires_prescription",
      "status",
      "generic_name",
      "composition",
      "dosage",
      "batch_number",
      "expiry_date",
    ];
    fields.forEach((f) => {
      if (payload[f] != null && payload[f] !== "") fd.append(f, payload[f]);
    });
    if (payload.image_url instanceof File)
      fd.append("image_url", payload.image_url);
    if (payload.medicine_images?.length) {
      payload.medicine_images.forEach((f) => fd.append("medicine_images[]", f));
    }
    const { data } = await api.put(`medicines/${id}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`medicines/${id}`);
    return data;
  },
  bulkUpload: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("medicines/admin/bulk-upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  downloadTemplate: async () => {
    const response = await api.get("medicines/admin/csv-template", {
      responseType: "blob",
    });
    const url = URL.createObjectURL(
      new Blob([response.data], { type: "text/csv" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "medicine_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  },
};

export const orderService = {
  ...mockService(mockOrders),
  getAll: async (params = {}) => {
    const { data } = await api.get("orders/admin/all", { params });
    return {
      orders: data?.data?.orders || [],
      pagination: data?.data?.pagination || {},
    };
  },
  assignDelivery: async (id, delivery_boy_id) => {
    const { data } = await api.patch(`orders/admin/${id}/assign`, {
      delivery_boy_id,
    });
    return data;
  },
  cancelOrder: async (id, reason) => {
    const { data } = await api.patch(`orders/admin/${id}/cancel`, { reason });
    return data;
  },
  markCODCollection: async (payload) => {
    const { data } = await api.post("orders/admin/collection", payload);
    return data;
  },
  getCODCollectionHistory: async (delivery_boy_id) => {
    const { data } = await api.get(
      `orders/admin/collection/${delivery_boy_id}`,
    );
    return data?.data || [];
  },
  updateStatus: async (id, status) => {
    const { data } = await api.patch(`orders/${id}/status`, { status });
    return data;
  },
  deliveryFailed: async (id, reason) => {
    const { data } = await api.patch(`orders/admin/${id}/failed`, { reason });
    return data;
  },
  placeOrder: async (payload) => {
    await delay();
    return payload;
  },
  verifyPrescription: async (orderId, status, note = "") => {
    await delay();
    return { orderId, status, note };
  },
};

export const paymentService = {
  getAll: async (params = {}) => {
    const { data } = await api.get("admin/payments", { params });
    console.log("service payment:", data);
    return {
      payments: data?.data?.payments || [],
      pagination: data?.data?.pagination || {},
    };
  },
};

export const walletService = {
  adjustBalance: async (deliveryBoyId, { type, amount, note }) => {
    await delay();
    const boy = mockDeliveryBoys.find(
      (d) => d.id === deliveryBoyId || d.id === parseInt(deliveryBoyId),
    );
    if (!boy) throw new Error("Delivery boy not found");
    const adj = parseFloat(amount);
    if (isNaN(adj) || adj <= 0) throw new Error("Invalid amount");
    if (type === "debit" && boy.walletBalance < adj)
      throw new Error("Insufficient balance");
    boy.walletBalance = +(
      type === "credit" ? boy.walletBalance + adj : boy.walletBalance - adj
    ).toFixed(2);
    const txn = {
      id: Date.now(),
      type,
      orderId: null,
      amount: adj,
      description:
        note ||
        (type === "credit" ? "Manual credit by admin" : "Deduction by admin"),
      date: new Date().toISOString().split("T")[0],
    };
    boy.walletTransactions.unshift(txn);
    return { walletBalance: boy.walletBalance, txn };
  },
};
export const dashboardService = {
  getStats: async () => {
    const { data } = await api.get("admin/dashboard");
    return data?.data || {};
  },
};

/* ── Deposit Requests Service ─────────────────────────────────────────────── */
export const depositRequestService = {
  getAll: async () => {
    await delay();
    return [...mockDepositRequests].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
  },
  getById: async (id) => {
    await delay();
    return mockDepositRequests.find((r) => r.id === id);
  },
  approve: async (id, adminNote = "") => {
    await delay();
    const req = mockDepositRequests.find((r) => r.id === id);
    if (!req) throw new Error("Request not found");
    if (req.status !== "pending")
      throw new Error("Only pending requests can be approved");
    req.status = "approved";
    req.adminNote = adminNote;
    req.processedDate = new Date().toISOString().split("T")[0];
    // Credit patient wallet
    const patient = mockPatients.find((p) => p.id === req.patientId);
    if (patient) {
      patient.walletBalance = +(
        (patient.walletBalance || 0) + req.amount
      ).toFixed(2);
      patient.totalDeposited = +(
        (patient.totalDeposited || 0) + req.amount
      ).toFixed(2);
      if (!patient.transactions) patient.transactions = [];
      patient.transactions.unshift({
        id: `TXN-D${String(Date.now()).slice(-4)}`,
        type: "Wallet Recharge",
        amount: req.amount,
        method: req.method,
        status: "completed",
        date: req.processedDate,
        reference: req.reference || `REF-DEP-${id}`,
        note: `Deposit approved — ${req.id}`,
      });
    }
    return req;
  },
  reject: async (id, adminNote = "") => {
    await delay();
    const req = mockDepositRequests.find((r) => r.id === id);
    if (!req) throw new Error("Request not found");
    if (req.status !== "pending")
      throw new Error("Only pending requests can be rejected");
    req.status = "rejected";
    req.adminNote = adminNote;
    req.processedDate = new Date().toISOString().split("T")[0];
    return req;
  },
};

/* ── Withdraw Requests Service ──────────────────────────────────────────── */
export const withdrawRequestsService = {
  getAll: async () => {
    await delay();
    const doctorRequests = mockDoctors.flatMap((d) =>
      (d.withdrawRequests || []).map((r) => ({
        ...r,
        userId: d.id,
        userName: d.name,
        userType: "Doctor",
        walletBalance: d.walletBalance,
      })),
    );
    const deliveryRequests = mockDeliveryBoys.flatMap((b) =>
      (b.withdrawRequests || []).map((r) => ({
        ...r,
        userId: b.id,
        userName: b.name,
        userType: "Delivery Boy",
        walletBalance: b.walletBalance,
      })),
    );
    return [...doctorRequests, ...deliveryRequests].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
  },
  approve: async (requestId, userType, userId) => {
    await delay();
    if (userType === "Doctor") {
      return doctorService.approveWithdraw(userId, requestId);
    } else {
      return deliveryBoyService.approveWithdraw(userId, requestId);
    }
  },
  reject: async (requestId, userType, userId) => {
    await delay();
    if (userType === "Doctor") {
      return doctorService.rejectWithdraw(userId, requestId);
    } else {
      return deliveryBoyService.rejectWithdraw(userId, requestId);
    }
  },
};

/* ── Qualification Service ──────────────────────────────────────────────── */
export const qualificationService = {
  getAll: async () => {
    const { data } = await api.get("doctors/admin/qualifications");
    return data?.data?.qualifications || [];
  },
  getActive: async () => {
    const { data } = await api.get("doctors/admin/qualifications");
    const quals = data?.data?.qualifications || [];
    return quals.filter((q) => q.status === "active");
  },
  create: async (name) => {
    const { data } = await api.post("doctors/admin/qualifications", { name });
    return data;
  },
  toggleStatus: async (id) => {
    const { data } = await api.patch(
      `doctors/admin/qualifications/${id}/toggle`,
    );
    return data;
  },
};

/* ── Specialization Service ─────────────────────────────────────────────── */
export const specializationService = {
  ...mockService(mockSpecializations),
  getAll: async () => {
    const { data } = await api.get("doctors/admin/specializations");
    return data?.data?.specializations || [];
  },
  getActive: async () => {
    const { data } = await api.get("doctors/admin/specializations");
    const specs = data?.data?.specializations || [];
    return specs.filter((s) => s.status === "active");
  },
  create: async (payload) => {
    const { data } = await api.post("doctors/admin/specializations", {
      name: payload.name,
    });
    return data;
  },
  toggleStatus: async (id) => {
    const { data } = await api.patch(
      `doctors/admin/specializations/${id}/toggle`,
    );
    return data;
  },
  bulkToggle: async (ids, status) => {
    await delay();
    ids.forEach((id) => {
      const s = mockSpecializations.find(
        (s) => s.id === id || s.id === parseInt(id),
      );
      if (s) s.status = status;
    });
    return true;
  },
};

/* ── Time Slot Service ──────────────────────────────────────────────────── */
export const timeSlotService = {
  ...mockService(mockTimeSlots),
  getActive: async () => {
    await delay();
    return mockTimeSlots.filter((s) => s.status === "active");
  },
  toggleStatus: async (id) => {
    await delay();
    const s = mockTimeSlots.find((s) => s.id === id || s.id === parseInt(id));
    if (s) s.status = s.status === "active" ? "inactive" : "active";
    return s;
  },
  bulkToggle: async (ids, status) => {
    await delay();
    ids.forEach((id) => {
      const s = mockTimeSlots.find((s) => s.id === id || s.id === parseInt(id));
      if (s) s.status = status;
    });
    return true;
  },
  checkOverlap: (startTime, endTime, excludeId = null) => {
    return mockTimeSlots.some((s) => {
      if (excludeId && (s.id === excludeId || s.id === parseInt(excludeId)))
        return false;
      // overlap: new start < existing end AND new end > existing start
      return startTime < s.endTime && endTime > s.startTime;
    });
  },
};

/* ── Banner Service ─────────────────────────────────────────────────────── */
export const bannerService = {
  getAll: async () => {
    const { data } = await api.get("sliders/admin?panel=user");
    return data?.data?.sliders || data?.data || [];
  },
  create: async (payload) => {
    const fd = new FormData();
    fd.append("panel", payload.panel);
    if (payload.image instanceof File) fd.append("image", payload.image);
    if (payload.title) fd.append("title", payload.title);
    if (payload.redirect_url) fd.append("redirect_url", payload.redirect_url);
    if (payload.sort_order != null) fd.append("sort_order", payload.sort_order);
    fd.append("is_active", payload.is_active ?? 1);
    const { data } = await api.post("sliders", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  update: async (id, payload) => {
    const fd = new FormData();
    if (payload.panel != null) fd.append("panel", payload.panel);
    if (payload.title != null) fd.append("title", payload.title);
    if (payload.redirect_url != null)
      fd.append("redirect_url", payload.redirect_url);
    if (payload.sort_order != null) fd.append("sort_order", payload.sort_order);
    if (payload.is_active != null) fd.append("is_active", payload.is_active);
    if (payload.image instanceof File) fd.append("image", payload.image);
    const { data } = await api.put(`sliders/admin/${id}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  toggleStatus: async (id, is_active) => {
    const { data } = await api.patch(`sliders/admin/${id}/status`, {
      is_active,
    });
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`sliders/admin/${id}`);
    return data;
  },

  /* ── Offer Banner Service ───────────────────────────────────── */

  getOfferBanners: async () => {
    const { data } = await api.get("offer-banner/admin/all");
    return data?.data?.sliders || data?.data || [];
  },

  createOfferBanner: async (payload) => {
    const fd = new FormData();

    fd.append("panel", "offer");

    if (payload.image instanceof File) {
      fd.append("banner", payload.image);
    }

    if (payload.title) {
      fd.append("title", payload.title);
    }

    if (payload.redirect_url) {
      fd.append("redirect_url", payload.redirect_url);
    }

    fd.append("is_active", payload.is_active ?? 1);

    const { data } = await api.post("offer-banner/admin/create", fd, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return data;
  },

  updateOfferBanner: async (id, payload) => {
    const fd = new FormData();

    fd.append("panel", "offer");

    if (payload.title != null) {
      fd.append("title", payload.title);
    }

    if (payload.redirect_url != null) {
      fd.append("redirect_url", payload.redirect_url);
    }

    if (payload.is_active != null) {
      fd.append("is_active", payload.is_active);
    }

    if (payload.image instanceof File) {
      fd.append("banner", payload.image);
    }

    const { data } = await api.put(`offer-banner/admin/update/${id}`, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return data;
  },

  deleteOfferBanner: async (id) => {
    const { data } = await api.delete(`offer-banner/admin/delete/${id}`);
    return data;
  },
};
/* ── Prescription banner Service ─────────────────────────────────────────────────────── */
export const prescriptionBannerService = {
  getAll: async () => {
    const { data } = await api.get("prescription-banner/admin");
    return data?.data?.banners || [];
  },
  create: async (payload) => {
    const fd = new FormData();
    if (payload.image instanceof File) fd.append("image", payload.image);
    fd.append("contact_number", payload.contact_number);
    fd.append("is_active", 1);
    const { data } = await api.post("prescription-banner/admin", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  update: async (id, payload) => {
    const fd = new FormData();
    if (payload.image instanceof File) fd.append("image", payload.image);
    if (payload.contact_number)
      fd.append("contact_number", payload.contact_number);
    const { data } = await api.put(`prescription-banner/admin/${id}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  toggleStatus: async (id, is_active) => {
    const { data } = await api.patch(`prescription-banner/admin/${id}/status`, {
      is_active,
    });
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`prescription-banner/admin/${id}`);
    return data;
  },
};

/* ── Offer Banner Service ───────────────────────────────────── */
export const offerBannerService = {
  getAll: async () => {
    const { data } = await api.get("offer-banner/admin");
    return data?.data?.banners || data?.data || [];
  },

  toggleStatus: async (id, is_active) => {
    const { data } = await api.patch(`offer-banner/admin/status/${id}`, {
      is_active,
    });
    return data;
  },
};

export const authService = {
  login: async (email, password) => {
    const { data } = await api.post("admin/auth/login", { email, password });
    return data;
  },
  changePassword: async (userId, password) => {
    const { data } = await api.patch(`admin/auth/users/${userId}/password`, {
      password,
    });
    return data;
  },
  sendPatientNotification: async (payload) => {
    const { data } = await api.post("admin/notifications/send-user", payload);

    return data;
  },
  sendDeliveryNotification: async (payload) => {
    const { data } = await api.post(
      "admin/notifications/send-delivery-boy",
      payload,
    );

    return data;
  },
};

/* ── Notification Service ─────────────────────────────────────────────── */
export const notificationService = {
  getAll: async (page = 1) => {
    const { data } = await api.get("admin/notifications", { params: { page } });
    return {
      notifications: data?.data?.notifications || [],
      unread_count: data?.data?.unread_count || 0,
      pagination: data?.data?.pagination || {},
    };
  },
  markRead: async (id) => {
    const { data } = await api.put(`admin/notifications/${id}/read`);
    return data;
  },
  markAllRead: async () => {
    const { data } = await api.put("admin/notifications/read-all");
    return data;
  },
};

/* ── Category Service ────────────────────────────────────────────────── */
export const categoryService = {
  getAll: async () => {
    const { data } = await api.get("medicines/admin/categories");
    return data?.data?.categories || data?.data || [];
  },
  create: async (name) => {
    const { data } = await api.post("medicines/categories", { name });
    return data;
  },
  toggleStatus: async (id, status) => {
    const { data } = await api.patch(`medicines/categories/${id}/status`, {
      status,
    });
    return data;
  },
};

/* ── withdraw request ────────────────────────────────────────────────── */
export const withdrawService = {
  getAll: async () => {
    const { data } = await api.get("admin/withdrawals");
    return data?.data?.withdrawals || [];
  },
  resolve: async (id, status, reject_reason = "") => {
    const { data } = await api.post(`doctor/withdraw/${id}/resolve`, {
      status, // 'completed' | 'rejected'
      reject_reason, // sirf reject ke waqt
    });
    return data;
  },
};

/* ── Brand Service ───────────────────────────────────────────────────── */
export const brandService = {
  getAll: async () => {
    const { data } = await api.get("medicines/admin/brands");
    return data?.data?.brands || data?.data || [];
  },
  create: async (name) => {
    const { data } = await api.post("medicines/brands", { name });
    return data;
  },
  toggleStatus: async (id, status) => {
    const { data } = await api.patch(`medicines/brands/${id}/status`, {
      status,
    });
    return data;
  },
};

export default api;

/* ── Delivery Boy Service ───────────────────────────────────────────────── */
export const deliveryBoyService = {
  ...mockService(mockDeliveryBoys),
  getAll: async (params = {}) => {
    const { data } = await api.get("admin/delivery-boys", { params });
    return data?.data?.delivery_boys || data?.data || [];
  },
  getById: async (id) => {
    const { data } = await api.get(`admin/delivery-boys/${id}`);
    return data?.data || null;
  },
  getActiveVerified: async () => {
    await delay();
    return mockDeliveryBoys.filter((d) => d.status === "active" && d.verified);
  },
  create: async (payload) => {
    const fd = new FormData();
    fd.append("name", payload.name);
    fd.append("mobile", payload.mobile);
    fd.append("email", payload.email);
    if (payload.bike_number) fd.append("bike_number", payload.bike_number);
    if (payload.adhar_front instanceof File)
      fd.append("adhar_front", payload.adhar_front);
    if (payload.adhar_back instanceof File)
      fd.append("adhar_back", payload.adhar_back);
    if (payload.pan_image instanceof File)
      fd.append("pan_image", payload.pan_image);
    if (payload.license_image instanceof File)
      fd.append("license_image", payload.license_image);
    const { data } = await api.post("admin/delivery-boys", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  update: async (id, payload) => {
    const fd = new FormData();
    if (payload.name) fd.append("name", payload.name);
    if (payload.mobile) fd.append("mobile", payload.mobile);
    if (payload.email) fd.append("email", payload.email);
    if (payload.bike_number) fd.append("bike_number", payload.bike_number);
    if (payload.adhar_front instanceof File)
      fd.append("adhar_front", payload.adhar_front);
    if (payload.adhar_back instanceof File)
      fd.append("adhar_back", payload.adhar_back);
    if (payload.pan_image instanceof File)
      fd.append("pan_image", payload.pan_image);
    if (payload.license_image instanceof File)
      fd.append("license_image", payload.license_image);
    const { data } = await api.put(`admin/delivery-boys/${id}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`admin/delivery-boys/${id}`);
    return data;
  },
  toggleStatus: async (id, status) => {
    const { data } = await api.patch(`admin/delivery-boys/${id}/status`, {
      status,
    });
    return data;
  },
  toggleVerified: async (id, is_verified) => {
    const { data } = await api.patch(`admin/delivery-boys/${id}/status`, {
      is_verified,
    });
    return data;
  },
  getAssignedOrders: async (id) => {
    await delay();
    const d = mockDeliveryBoys.find(
      (d) => d.id === id || d.id === parseInt(id),
    );
    if (!d) return [];
    return mockOrders.filter((o) => d.assignedOrders.includes(o.id));
  },
  requestWithdraw: async (deliveryBoyId, amount) => {
    await delay();
    const boy = mockDeliveryBoys.find((d) => d.id === parseInt(deliveryBoyId));
    if (!boy) throw new Error("Delivery boy not found");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) throw new Error("Invalid amount");
    if (amt > boy.walletBalance)
      throw new Error("Amount exceeds wallet balance");
    const req = {
      id: `WR-D${String(Date.now()).slice(-4)}`,
      amount: amt,
      status: "pending",
      date: new Date().toISOString().split("T")[0],
      processedDate: null,
    };
    if (!boy.withdrawRequests) boy.withdrawRequests = [];
    boy.withdrawRequests.unshift(req);
    return req;
  },
  approveWithdraw: async (deliveryBoyId, requestId) => {
    await delay();
    const boy = mockDeliveryBoys.find((d) => d.id === parseInt(deliveryBoyId));
    if (!boy) throw new Error("Delivery boy not found");
    const req = boy.withdrawRequests?.find((r) => r.id === requestId);
    if (!req) throw new Error("Request not found");
    if (req.status !== "pending") throw new Error("Request is not pending");
    if (req.amount > boy.walletBalance) throw new Error("Insufficient balance");
    boy.walletBalance = +(boy.walletBalance - req.amount).toFixed(2);
    boy.totalWithdrawn = +((boy.totalWithdrawn || 0) + req.amount).toFixed(2);
    req.status = "approved";
    req.processedDate = new Date().toISOString().split("T")[0];
    return req;
  },
  rejectWithdraw: async (deliveryBoyId, requestId) => {
    await delay();
    const boy = mockDeliveryBoys.find((d) => d.id === parseInt(deliveryBoyId));
    if (!boy) throw new Error("Delivery boy not found");
    const req = boy.withdrawRequests?.find((r) => r.id === requestId);
    if (!req) throw new Error("Request not found");
    if (req.status !== "pending") throw new Error("Request is not pending");
    req.status = "rejected";
    req.processedDate = new Date().toISOString().split("T")[0];
    return req;
  },
};
export const labTestCategoryService = {
  ...mockService(mockLabTestCategories),
  getAll: async () => {
    const { data } = await api.get("lab-tests/admin/categories");
    return data?.data?.categories || data?.data || [];
  },
  getActive: async () => {
    const { data } = await api.get("lab-tests/admin/categories");
    const cats = data?.data?.categories || data?.data || [];
    return cats.filter((c) => c.is_active === 1 || c.status === "active");
  },
  create: async (payload) => {
    const { data } = await api.post("lab-tests/categories", {
      name: payload.name,
      sort_order: payload.sort_order ?? 1,
      is_active: payload.is_active ?? 1,
    });
    return data;
  },
  toggleStatus: async (id, currentStatus) => {
    const is_active = currentStatus === "active" || currentStatus === 1 ? 0 : 1;
    const { data } = await api.patch(
      `lab-tests/admin/categories/${id}/status`,
      { is_active },
    );
    return data;
  },
};

/* ── Lab Test Service ───────────────────────────────────────────────────── */
export const labTestService = {
  ...mockService(mockLabTests),
  getAll: async (params = {}) => {
    const { data } = await api.get("lab-tests/admin", { params });
    return data?.data?.tests || data?.data || [];
  },
  getSlots: async (id) => {
    const { data } = await api.get(`lab-tests/${id}/admin/slots`);
    return data?.data?.slots || {};
  },
  saveSlots: async (id, slots) => {
    const { data } = await api.post(`lab-tests/${id}/slots`, { slots });
    return data;
  },
  create: async (payload) => {
    const fd = new FormData();
    fd.append("category_id", payload.categoryId ?? payload.category_id ?? "");
    fd.append("name", payload.name);
    fd.append("price", payload.price);
    fd.append("discounted_price", payload.discounted_price ?? "");
    fd.append("description", payload.description || "");
    fd.append(
      "discount_percent",
      payload.discount ?? payload.discount_percent ?? 0,
    );
    fd.append("home_collection", payload.home_collection ? 1 : 0);
    fd.append("sort_order", payload.sort_order ?? "");
    fd.append("is_active", payload.is_active === 1 ? 1 : 0);
    if (payload.image instanceof File) fd.append("image", payload.image);
    const { data } = await api.post("lab-tests", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  toggleStatus: async (id, currentStatus) => {
    const is_active = currentStatus === "active" || currentStatus === 1 ? 0 : 1;
    const { data } = await api.patch(`lab-tests/admin/${id}/status`, {
      is_active,
    });
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`lab-tests/admin/${id}`);
    return data;
  },
  toggleFeatured: async (id) =>{
    await delay();
    const t = mockLabTests.find((t) => t.id === id || t.id === parseInt(id));
    if (t) t.featured = !t.featured;
    return t;
  },
  bulkUpload: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post('lab-tests/admin/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  downloadTemplate: async () => {
    const response = await api.get('lab-tests/admin/bulk-upload/template', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const a   = document.createElement('a');
    a.href = url; a.download = 'lab_tests_template.csv'; a.click();
    URL.revokeObjectURL(url);
  },
  update: async (id, payload) => {
    const fd = new FormData();

    // category_id — payload.categoryId ya payload.category_id dono handle karo
    const categoryId = payload.categoryId ?? payload.category_id;
    if (categoryId != null && categoryId !== "")
      fd.append("category_id", categoryId);

    // Simple fields — sirf ek baar
    if (payload.name != null && payload.name !== "")
      fd.append("name", payload.name);
    if (payload.price != null && payload.price !== "")
      fd.append("price", payload.price);
    if (payload.description != null)
      fd.append("description", payload.description || "");
    if (payload.sort_order != null && payload.sort_order !== "")
      fd.append("sort_order", payload.sort_order);

    // discounted_price — sirf ek baar
    const discountedPrice = payload.discounted_price;
    if (discountedPrice != null && discountedPrice !== "")
      fd.append("discounted_price", discountedPrice);

    // discount_percent — payload.discount ya payload.discount_percent
    const discountPercent = payload.discount ?? payload.discount_percent;
    if (discountPercent != null && discountPercent !== "")
      fd.append("discount_percent", discountPercent);

    // home_collection — sirf ek baar
    const homeCollection =
      payload.homeCollection !== undefined
        ? payload.homeCollection
        : payload.home_collection;
    if (homeCollection !== undefined)
      fd.append("home_collection", homeCollection ? 1 : 0);

    // is_active — payload.is_active ya payload.status se — sirf ek baar
    let isActive;
    if (payload.is_active !== undefined) isActive = payload.is_active;
    else if (payload.status !== undefined)
      isActive = payload.status === "active" ? 1 : 0;
    if (isActive !== undefined) fd.append("is_active", isActive);

    // Image
    if (payload.image instanceof File) fd.append("image", payload.image);

    const { data } = await api.put(`lab-tests/admin/${id}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
};

/* ── Lab Booking Service ─────────────────────────────────────────────────────────── */
/* ── Support Ticket Service ─────────────────────────────────────────────── */
export const supportTicketService = {
  getAll: async () => {
    const { data } = await api.get("support/admin");
    return data?.data?.tickets || [];
  },
  getById: async (id) => {
    const { data } = await api.get(`support/admin/${id}`);
    return data?.data?.ticket || null;
  },
  resolve: async (id, status, admin_note) => {
    const { data } = await api.post(`support/${id}/resolve`, {
      status,
      admin_note,
    });
    return data;
  },
  create: async (payload) => {
    await delay();
    mockSupportTickets.unshift(payload);
    return payload;
  },
  updateStatus: async (id, status) => {
    await delay();
    const t = mockSupportTickets.find((t) => t.id === id);
    if (!t) throw new Error("Ticket not found");
    t.status = status;
    t.updatedAt = new Date().toISOString();
    return t;
  },
  addNote: async (id, text, addedBy = "Admin") => {
    await delay();
    const t = mockSupportTickets.find((t) => t.id === id);
    if (!t) throw new Error("Ticket not found");
    const note = {
      id: Date.now(),
      text,
      addedBy,
      timestamp: new Date().toISOString(),
    };
    t.notes.push(note);
    t.updatedAt = new Date().toISOString();
    return note;
  },
  assignTo: async (id, assignedTo) => {
    await delay();
    const t = mockSupportTickets.find((t) => t.id === id);
    if (!t) throw new Error("Ticket not found");
    t.assignedTo = assignedTo;
    t.updatedAt = new Date().toISOString();
    return t;
  },
  updatePriority: async (id, priority) => {
    await delay();
    const t = mockSupportTickets.find((t) => t.id === id);
    if (!t) throw new Error("Ticket not found");
    t.priority = priority;
    t.updatedAt = new Date().toISOString();
    return t;
  },
};

export const labBookingService = {
  getAll: async () => {
    const { data } = await api.get("lab-test-orders/admin/bookings");
    return data?.data?.bookings || data?.data || [];
  },
  getPayments: async () => {
    const { data } = await api.get("lab-test-orders/admin/payments");
    return {
      payments: data?.data?.payments || [],
      summary: data?.data?.summary || {},
    };
  },
  updateStatus: async (id, status) => {
    const { data } = await api.patch(
      `lab-test-orders/admin/bookings/${id}/status`,
      { status },
    );
    return data;
  },
  uploadReport: async (id, file) => {
    const fd = new FormData();
    fd.append("report", file);
    const { data } = await api.post(
      `lab-test-orders/admin/bookings/${id}/complete`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },

  getById: async (id) => {
    const { data } = await api.get(`lab-test-orders/admin/bookings/${id}`);
    return data?.data?.booking || null;
  },

  getByPatient: async (patientId) => {
    await delay();
    return mockLabBookings.filter((b) => b.patientId === parseInt(patientId));
  },

  create: async (payload) => {
    await delay();
    const booking = {
      ...payload,
      id: `LB-${String(mockLabBookings.length + 1).padStart(3, "0")}`,
      status: "booked",
      report: null,
      statusHistory: [
        {
          status: "booked",
          updatedBy: "Admin",
          note: "Booking created by admin",
          timestamp: new Date().toISOString(),
        },
      ],
      createdAt: new Date().toISOString(),
    };
    mockLabBookings.push(booking);
    return booking;
  },

  updateCollection: async (id, collectionData) => {
    await delay();
    const booking = mockLabBookings.find((b) => b.id === id);
    if (!booking) throw new Error("Booking not found");
    booking.collection = { ...booking.collection, ...collectionData };
    return booking;
  },

  assignLabPartner: async (id, labPartner) => {
    await delay();
    const booking = mockLabBookings.find((b) => b.id === id);
    if (!booking) throw new Error("Booking not found");
    booking.labPartner = labPartner;
    return booking;
  },

  updatePaymentStatus: async (id, paymentStatus) => {
    await delay();
    const booking = mockLabBookings.find((b) => b.id === id);
    if (!booking) throw new Error("Booking not found");
    const prev = booking.paymentStatus;
    booking.paymentStatus = paymentStatus;
    // if marking completed, add to patient transactions
    if (paymentStatus === "completed" && prev !== "completed") {
      const patient = mockPatients.find((p) => p.id === booking.patientId);
      if (patient) {
        patient.totalSpent = +(
          (patient.totalSpent || 0) + booking.amount
        ).toFixed(2);
        if (!patient.transactions) patient.transactions = [];
        patient.transactions.unshift({
          id: `TXN-LB${String(Date.now()).slice(-4)}`,
          type: "Lab Test",
          amount: booking.amount,
          method: booking.paymentMethod,
          status: "completed",
          date: new Date().toISOString().split("T")[0],
          reference: `REF-${booking.id}`,
          bookingId: booking.id,
        });
      }
    }
    return booking;
  },
};

export const couponService = {
  getAll: async () => {
    const { data } = await api.get("coupon/admin/all");
    return data?.data || [];
  },
  create: async (payload) => {
    const { data } = await api.post("coupon/admin/create", payload);
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`coupon/admin/delete/${id}`);
    return data;
  },
};

export const policyService = {
  // Get All Policies Tabs
  getAllPolicies: async () => {
    const { data } = await api.get("policies");
    return data?.data?.policies || [];
  },

  // Get Single Policy Details
  getPolicyByKey: async (key) => {
    const { data } = await api.get(`policies/${key}`);
    return data?.data?.policy || null;
  },

  // Update Policy
  updatePolicy: async (policyKey, payload) => {
    const { data } = await api.put(`policies/${policyKey}`, {
      title: payload.title,
      content: payload.content,
    });

    return data;
  },
};